import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from '@angular/core';
import { TRANSLOCO_SCOPE, TranslocoDirective } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { SubscriptionBlockReason, SubscriptionClientService } from '../../core/api';
import { ILogger } from '../../interfaces/logger.interface';
import { AuthService } from '../../services/auth.service';
import { ConfigService } from '../../services/config.service';
import { SubscriptionPortalService } from '../../services/subscription-portal.service';
import { UserService } from '../../services/user.service';
import { LegalLinksContainerComponent } from '../../shared/components/legal-links-container/legal-links-container.component';
import { LoaderComponent } from '../../shared/components/loader/loader.component';
import { LoggerFactory } from '../../utils/logger-factory';

const PRICING_TABLE_SCRIPT_SRC = 'https://js.stripe.com/v3/pricing-table.js';

@Component({
  selector: 'app-subscription-expired',
  standalone: true,
  imports: [CommonModule, TranslocoDirective, LoaderComponent, LegalLinksContainerComponent],
  templateUrl: './subscription-expired.component.html',
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'subscription-expired' }],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SubscriptionExpiredComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _userService = inject(UserService);
  private readonly _subscriptionClient = inject(SubscriptionClientService);
  private readonly _subscriptionPortalService = inject(SubscriptionPortalService);
  private readonly _logger: ILogger = LoggerFactory.getLogger('SubscriptionExpiredComponent');

  /** t(subscriptionExpired.reason.TrialExpired) */
  /** t(subscriptionExpired.reason.Unpaid) */
  /** t(subscriptionExpired.reason.Paused) */
  /** t(subscriptionExpired.reason.CanceledAndEnded) */
  /** t(subscriptionExpired.reason.NoSubscription) */

  /**
   * Block reasons where an existing Stripe subscription can still be salvaged in the customer portal —
   * the user only has to add a payment method, settle an invoice or resume collection.
   *
   * Everything else (cancelled and expired, or never subscribed) leaves nothing for the portal to manage:
   * Stripe shows an empty portal for a fully ended subscription, which is a dead end. Those cases need a
   * plan to be chosen again, which is what the pricing table is for.
   */
  private static readonly PORTAL_REASONS: readonly SubscriptionBlockReason[] = [
    SubscriptionBlockReason.TrialExpired,
    SubscriptionBlockReason.Unpaid,
    SubscriptionBlockReason.Paused
  ];

  public canUsePortal = false;
  public isOpeningPortal = false;
  public reasonKey = 'reason.NoSubscription';

  public clientSecret: string | null = null;
  public publishableKey = ConfigService.getStripePublishableKey();
  public pricingTableId = ConfigService.getStripePricingTableId();
  public isLoading = true;
  public hasError = false;
  public errorKey = 'errors.sessionLoad';

  async ngOnInit(): Promise<void> {
    await this._resolveState();
  }

  public async openCustomerPortal(): Promise<void> {
    if (this.isOpeningPortal) {
      return;
    }
    try {
      this.isOpeningPortal = true;
      await this._subscriptionPortalService.openCustomerPortal();
    } finally {
      this.isOpeningPortal = false;
    }
  }

  public async logout(): Promise<void> {
    await this._authService.logout();
  }

  private async _resolveState(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    const reason = this._userService.currentUser?.subscriptionBlockReason ?? SubscriptionBlockReason.NoSubscription;
    this.reasonKey = `reason.${reason}`;

    await this._userService.loadSubscriptionData(true);

    const subscription = this._userService.getCurrentSubscription();

    // No subscription row at all: neither path works. The portal has nothing to manage, and the pricing
    // table needs a CustomerSession, which the backend refuses without a Stripe customer. Say so instead of
    // firing a request that is guaranteed to fail.
    if (!subscription?.stripeCustomerId) {
      this.errorKey = 'errors.noSubscription';
      this.hasError = true;
      this.isLoading = false;
      return;
    }

    // Deciding on the reason rather than on "a Stripe customer exists": a cancelled-and-expired customer
    // still has one, so keying on that alone sent exactly the people who need to pick a new plan into an
    // empty portal.
    this.canUsePortal = SubscriptionExpiredComponent.PORTAL_REASONS.includes(reason);

    if (this.canUsePortal) {
      this.isLoading = false;
      return;
    }

    this._ensurePricingTableScript();
    await this._loadCustomerSession();
  }

  private async _loadCustomerSession(): Promise<void> {
    if (!this.publishableKey || !this.pricingTableId) {
      this._logger.writeError('Stripe pricing table not configured (publishableKey / pricingTableId missing)');
      this.hasError = true;
      this.isLoading = false;
      return;
    }

    try {
      const result = await firstValueFrom(this._subscriptionClient.createCustomerSession());
      this.clientSecret = result?.clientSecret ?? null;
      if (!this.clientSecret) {
        throw new Error('No clientSecret returned');
      }
    } catch (error) {
      this._logger.writeError('Failed to create Stripe customer session', error);
      this.errorKey = 'errors.sessionLoad';
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  private _ensurePricingTableScript(): void {
    if (document.querySelector(`script[src="${PRICING_TABLE_SCRIPT_SRC}"]`)) {
      return;
    }
    const script = document.createElement('script');
    script.src = PRICING_TABLE_SCRIPT_SRC;
    script.async = true;
    document.head.appendChild(script);
  }
}
